import { useState, useEffect, useContext, useMemo } from 'react';
import cx from 'classnames';
import { useRouter } from 'next/router';
import { useTranslations, useFormatter } from 'next-intl';
import { useInView } from 'react-intersection-observer';
import {
  IArticalAuther, IForumType, IChannelTagType, ChannelTagDisplayTypeEnum,
} from '@/api/detail';
import CommonIcon from '@/components/CommonIcon';
import MoreActionIcon from '@/components/MoreActionIcon/mIndex';
import Tips from '@/components/Tips/mIndex';
import Ptips from '@/components/Tips/index';
import ls from '@/utils/localStorage';
import { formatDisplayNumber } from '@/utils/formatNumber';
import formatDateHandler from '@/utils/formatDate';
import { namespace, ActionTypeValues, StoreContext, IAction } from '@/context';
import { isStoreAppBannerEntry, isMimoHomeListEntry } from '@/utils/url';
import Styles from './mIndex.module.scss';
import { ThreadDetailCtx } from '@/context/threadDetail';
import { pagePathUrl } from '@/constants/url';

export interface IThreadRelatedInfo {
  className?: string;
  devices?: Array<NonNullable<IArticalAuther['device']>>;
  subjects?: IForumType[];
  channel_tags?: IChannelTagType[];
  dateline: number;
  views: number;
  comments: number;
  isLoggin?: boolean;
  isMobile?: boolean;
  showMoreAction?: boolean;
  onClickUserAction?: () => void;
  /** 禁用主题分类的点击事件 */
  disableSubjectClick?: boolean;
}

interface ISubject {
  url: string;
  name: string;
  key: string;
  fid: number;
}

const ThreadRelatedInfo = ({
  className = '',
  subjects = [],
  channel_tags = [],
  dateline,
  views,
  comments,
  isLoggin = false,
  isMobile = true,
  showMoreAction = true,
  onClickUserAction = () => {},
  disableSubjectClick,
}: IThreadRelatedInfo) => {
  const { state, dispatch } = useContext(StoreContext);
  const [mobile, setMobile] = useState<boolean | null>(null);
  const t = useTranslations();
  const formatter = useFormatter();
  const router = useRouter();
  const { track, TRACK_EVENTS, extractThreadTags } = state[namespace.app].sensors || {};

  const threadDetail = useContext(ThreadDetailCtx)

  const totalReplies = useMemo(() => {
    const total = state.detail.totalReplys || comments
    return total
  }, [comments, state.detail.totalReplys]);

  // 推荐内容/分类 这里展示频道+一级分类
  const handleSubjects = (): any[] => {
    const subjectsArr: ISubject[] = [];
    const channelTagsArr: {}[] = [];

    // PC
    // Mobile
    (channel_tags || []).forEach(({ slug, name, display_type, channel_path = [] }) => {
      let url, ns, actions: Array<IAction<any>>;

      if (display_type === ChannelTagDisplayTypeEnum.ACTIVITY) {
        url = pagePathUrl.activity;
        actions = [
          {
            namespace: namespace.activity,
            type: ActionTypeValues.UPDATE_ACTIVITY_CHANNEL_ACTIVE_KEYS,
            payload: channel_path.length > 2 ? channel_path.slice(0, 2).reverse() : channel_path.slice(0, 1),
          },
          {
            namespace: namespace.activity,
            type: ActionTypeValues.UPDATE_ACTIVITY_CHANNEL_SORT_KEY_MAP,
            payload: {},
          },
        ];
      } else if (display_type === ChannelTagDisplayTypeEnum.GROUP) {
        url = `${pagePathUrl.devicesSeries}?series=${slug}`;
        actions = [];
      } else if (display_type === ChannelTagDisplayTypeEnum.THEME) {
        url = `${pagePathUrl.device}/${slug}`;
        actions = [];
      } else {
        url = pagePathUrl.home;
        actions = [];
      }

      channelTagsArr.push({
        slug,
        name,
        url,
        ns,
        actions,
        type: display_type,
      });
    });

    return channelTagsArr;
  }

  const { ref, inView } = useInView({
    threshold: 0,
  });

  useEffect(() => {
    setMobile(isMobile);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClickSubject = (name: string, url: string) => {
    isMobile ? router.push(url) : window.open(url);
  };

  const handleClickUserAction = () => {
    onClickUserAction();
  };

  const [pcTipsShown, setPcTipsShown] = useState(ls.get('pc-detail-tips')?.done);
  const renderPcTips = () => {
    if (!inView) {
      return null;
    }
    if (!mobile && !pcTipsShown) {
      return <Ptips className={Styles.pcTips} tips={t('tips_device')} direction='bottom' arrowPosition='left' onClose={() => {
        setPcTipsShown(true);
        ls.set('pc-detail-tips', { done: true });
      }} />
    }
    return null;
  }
  const renderMobileTips = () => {
    if(disableSubjectClick){
      return null;
    }
    if (!inView) {
      return null;
    }
    if (mobile) {
      return !ls.get('m-detail-tips')?.done && <Tips className={Styles.tips} tips={t('tips_device')} />
    }
    return null;
  }

  const renderRecommend = () => {
    const isEmpty = isMobile ? channel_tags.length <= 0 : subjects.length <= 0;
    // 相关分类
    const subjectsDom = !isEmpty && (
      <div id="subjects-wrapper" ref={ref} className={cx(Styles.subjectsWrapper, !isMobile && Styles.isPC)} key="subjects">
        <div className={Styles.subjects}>
          {
            handleSubjects().map(({
                slug, name, url, actions, type,
              }) => {
                return (
                  <div
                    key={slug}
                    className={cx(Styles.subject, {[Styles.iconSubject]: (type === ChannelTagDisplayTypeEnum.ACTIVITY ||
                    type === ChannelTagDisplayTypeEnum.GROUP)})}
                    onClick={() => {
                      if(disableSubjectClick) return;
                      track?.(TRACK_EVENTS?.[namespace.detail].tagsPageDetailEntryClick.name, {
                        tag_name: name,
                        thread_tid: threadDetail?.tid,
                        thread_name: threadDetail?.subject,
                        account_slug: threadDetail?.authorid || threadDetail?.author?.id,
                        thread_tag_name: extractThreadTags?.(threadDetail?.forum_types),
                        is_featured: !!threadDetail?.digest,
                        list_name: (threadDetail?.forum_types || [])[0]?.name || t('btn_home'),
                      }, namespace.detail);
                      isMobile && dispatch(actions);
                      isMobile ? router.push(url) : window.open(url);
                    }}
                  >
                    {
                      (type === ChannelTagDisplayTypeEnum.ACTIVITY || type === ChannelTagDisplayTypeEnum.GROUP) && (
                        <div className={cx(Styles.iconWrapper, Styles[type])}>
                          <CommonIcon className={Styles.subjectIcon} icon={type} />
                        </div>
                      )
                    }
                    {name}
                  </div>
                )
              })
          }
        </div>
      </div>
    );

    // 时间 浏览 评论 更多按钮
    const timeAndViewsDom = <div className={Styles.extra} key="extra">
      <div className={Styles.left}>
        <div className={Styles.time}>{t('label_thread_time')}<span className={Styles.text}>{formatDateHandler(dateline, formatter)}</span></div>
        {!!views && <div className={Styles.views}>{t('label_views')}<span className={Styles.text}>{formatDisplayNumber(views)}</span></div>}
        { mobile && !!totalReplies && <div className={Styles.comments}>{t('label_comments')}<span className={Styles.text}>{formatDisplayNumber(totalReplies)}</span></div>}
      </div>
      {
        isLoggin && showMoreAction && <MoreActionIcon className={Styles.right} onClick={handleClickUserAction} />
      }
    </div>;

    const mobileRecomendDom = isMimoHomeListEntry() ? null : (isStoreAppBannerEntry(router.query.entry) ? <>{timeAndViewsDom}</> : <>{subjectsDom}{timeAndViewsDom}</>);
    return mobile ? mobileRecomendDom : <>{subjectsDom}{timeAndViewsDom}</>;
  }

  // const renderDevices = () => {
  //   if(!devices || devices.length <= 0) {
  //     return null;
  //   }
  //   if (mobile) {
  //     return (
  //       <div className={Styles.mDevices}>
  //         <div className={Styles.note}>{t('tips_topic_related_device')}</div>
  //         <div className={Styles.name}>{devices.map(({ name }) => name).join('，')}</div>
  //       </div>
  //     )
  //   }
  //   return (
  //     <div className={Styles.devices}>
  //       {t('tips_topic_related_device')}{devices.map(({ name }) => name).join('，')}
  //     </div>
  //   );
  // }

  return (
    <div className={cx(Styles.root, className)}>
      {/* {renderDevices()} */}
      {/* 这里pc和m的展示顺序不一样 */}
      { renderRecommend() }
    </div>
  );
};

export default ThreadRelatedInfo;
